import { useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Check, Minus, Plus, Trash2 } from 'lucide-react'
import { isManager, useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'
import SettingsSection, {
  SettingsEmpty, SettingsSkeleton,
  settingsAddRow, settingsAddButton, settingsIconButton, settingsInput, settingsRow,
} from '@/components/settings/SettingsSection'
import EmojiGlyph from '@/components/ui/EmojiGlyph'
import type { DefaultSquad } from '@/lib/squadTypes'
import { defaultSlots, orderedSlots, roleOf, typeOf, useRosterCatalog } from '@/lib/rosterCatalog'

function countOf(sq: DefaultSquad, role: string) {
  return sq.slots.filter(r => r === role).length
}

/**
 * Settings → Varsayılan Kadro: the squad layout a match roster opens with.
 *
 * Edited as one draft and saved with one button — a layout is a single
 * document, and saving it squad by squad would leave half-edited defaults
 * live between clicks. A match whose roster was already saved keeps its own.
 */
export default function RosterDefaultSettings() {
  const { user } = useAuth()
  const { T } = useLanguage()
  const canEdit = isManager(user)
  const catalog = useRosterCatalog()

  const [draft, setDraft] = useState<DefaultSquad[]>([])
  const [saved, setSaved] = useState<string>('')        // JSON of what the server holds
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)

  useEffect(() => {
    fetch('/api/roster-default', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { squads: [] }))
      .then((d: { squads?: DefaultSquad[] }) => {
        const squads = d.squads ?? []
        setDraft(squads)
        setSaved(JSON.stringify(squads))
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const dirty = JSON.stringify(draft) !== saved
  const slotTotal = useMemo(() => draft.reduce((n, sq) => n + sq.slots.length, 0), [draft])

  const update = (i: number, patch: Partial<DefaultSquad>) => {
    setDraft(prev => prev.map((sq, j) => (j === i ? { ...sq, ...patch } : sq)))
    setJustSaved(false)
  }

  const adjust = (i: number, role: string, delta: number) => {
    const sq = draft[i]
    const type = typeOf(catalog, sq.type)
    const counts: Record<string, number> = {}
    for (const rule of type?.rules ?? []) counts[rule.role] = countOf(sq, rule.role)
    counts[role] = (counts[role] ?? 0) + delta
    update(i, { slots: orderedSlots(type, counts) })
  }

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir
    if (j < 0 || j >= draft.length) return
    setDraft(prev => {
      const next = [...prev]
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })
    setJustSaved(false)
  }

  const remove = (i: number) => {
    setDraft(prev => prev.filter((_, j) => j !== i))
    setJustSaved(false)
  }

  const add = (typeKey: string) => {
    const type = typeOf(catalog, typeKey)!
    const n = draft.filter(sq => sq.type === typeKey).length + 1
    setDraft(prev => [...prev, {
      name: `${type.name} ${n}`,
      type: typeKey,
      slots: defaultSlots(type),
    }])
    setJustSaved(false)
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/roster-default', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ squads: draft.map(sq => ({ ...sq, name: sq.name.trim() })) }),
      })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        setError(d.detail ?? T.settings.rosterDefaultSaveError)
        return
      }
      const d: { squads: DefaultSquad[] } = await res.json()
      // The server drops unnamed squads; show what was actually stored.
      setDraft(d.squads)
      setSaved(JSON.stringify(d.squads))
      setJustSaved(true)
    } catch {
      setError(T.settings.rosterDefaultSaveError)
    } finally {
      setBusy(false)
    }
  }

  return (
    <SettingsSection title={T.settings.rosterDefault} description={T.settings.rosterDefaultDesc}>
      {loading ? <SettingsSkeleton rows={5} /> : (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              {T.settings.rosterDefaultSummary
                .replace('{squads}', String(draft.length))
                .replace('{slots}', String(slotTotal))}
            </span>
          </div>

          {draft.length === 0 ? (
            <SettingsEmpty>{T.settings.rosterDefaultEmpty}</SettingsEmpty>
          ) : (
            <div className="space-y-2">
              {draft.map((sq, i) => {
                const type = typeOf(catalog, sq.type)
                const rules = type?.rules ?? []
                return (
                  <div key={i} className={`${settingsRow} flex-wrap`}>
                    <span className="flex w-5 shrink-0 justify-center"><EmojiGlyph emoji={type?.emoji} size={18} /></span>
                    <input
                      value={sq.name}
                      disabled={!canEdit}
                      onChange={e => update(i, { name: e.target.value })}
                      placeholder={T.settings.rosterDefaultNamePh}
                      maxLength={40}
                      className={`${settingsInput} w-36 min-w-0 flex-1 sm:flex-none disabled:opacity-80`}
                    />
                    <div className="flex flex-1 flex-wrap items-center gap-x-3 gap-y-1">
                      {!type && catalog.squad_types.length > 0 && (
                        <span className="text-xs text-amber-400/80">{T.settings.rosterDefaultUnknownType}</span>
                      )}
                      {rules.map(rule => {
                        const n = countOf(sq, rule.role)
                        const role = roleOf(catalog, rule.role)
                        return (
                          <span key={rule.role} className="flex items-center gap-1 text-xs text-muted-foreground" title={role?.name}>
                            <EmojiGlyph emoji={role?.emoji} size={16} />
                            <span className="hidden md:inline">{role?.name ?? rule.role}</span>
                            {rule.min === rule.max || !canEdit ? (
                              <span className="w-5 text-center tabular-nums text-foreground/80">{n}</span>
                            ) : (
                              <>
                                <button className={settingsIconButton + ' !h-6 !w-6'} disabled={n <= (rule.min ?? 0)}
                                  onClick={() => adjust(i, rule.role, -1)} aria-label="-">
                                  <Minus className="h-3 w-3" />
                                </button>
                                <span className="w-4 text-center tabular-nums text-foreground/80">{n}</span>
                                <button className={settingsIconButton + ' !h-6 !w-6'} disabled={n >= (rule.max ?? 99)}
                                  onClick={() => adjust(i, rule.role, 1)} aria-label="+">
                                  <Plus className="h-3 w-3" />
                                </button>
                              </>
                            )}
                          </span>
                        )
                      })}
                    </div>
                    {canEdit && (
                      <div className="ml-auto flex items-center">
                        <button className={settingsIconButton} disabled={i === 0} onClick={() => move(i, -1)} aria-label="up">
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button className={settingsIconButton} disabled={i === draft.length - 1} onClick={() => move(i, 1)} aria-label="down">
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button className={`${settingsIconButton} hover:!text-destructive`} onClick={() => remove(i)} aria-label="delete">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {canEdit && (
            <>
              <div className={settingsAddRow}>
                <span className="px-1 text-xs text-muted-foreground/60">{T.settings.rosterDefaultAdd}</span>
                {catalog.squad_types.map(t => (
                  <button key={t.key} onClick={() => add(t.key)}
                    className="flex h-8 items-center gap-1.5 rounded border border-white/10 bg-white/5 px-2.5 text-xs text-foreground/80 transition-colors hover:border-white/20 hover:bg-white/10">
                    <EmojiGlyph emoji={t.emoji} size={16} />
                    {t.name}
                  </button>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                {error && <p className="mr-auto text-xs text-destructive">{error}</p>}
                {justSaved && !dirty && (
                  <span className="mr-auto flex items-center gap-1 text-xs text-green-400/80">
                    <Check className="h-3.5 w-3.5" />{T.settings.rosterDefaultSaved}
                  </span>
                )}
                <button onClick={save} disabled={busy || !dirty} className={settingsAddButton}>
                  {busy ? T.settings.rosterDefaultSaving : T.settings.rosterDefaultSave}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </SettingsSection>
  )
}
